<template>
  <div v-loading.fullscreen="isWarehouseLoading" class="flex flex-col responsive-page fixed-table">
    <div class="header-container-wrapper">
      <div class="flex items-center pb-8 border-solid border-b-1 border-gray-300 header-container">
        <div class="flex-grow items-center flex header-container__left">
          <PageHeader :title="$t('warehouses.warehouse')" @load:refreshList="fetchList" />
        </div>
        <div class="flex header-container__right">
          <el-input
            class="w-60"
            prefix-icon="el-icon-search"
            v-model="dataSearch.code"
            @keyup="refreshWarehouseList"
            :placeholder="$t('warehouses.search_placeholder')"
          />
        </div>
      </div>
    </div>

    <div v-if="getWarehouses.length > 0">
      <div class="full-page-width flex justify-between table-actions py-5">
        <span class="font-bold text-xl">{{ $t('warehouses.warehouse_list') }}</span>
        <el-button @click="exportCSV" class="excel-export-btn">{{ $t('barcode.excel_export') }}</el-button>
      </div>
      <div class="mb-16 flex-grow">
        <WarehouseList ref="warehouseListRef" :warehousesData="getWarehouses" @sort-change="handleSortChange" />
      </div>
      <div class="justify-center flex flex-row mb-16 paging-container">
        <el-pagination
          layout="prev, pager, next"
          :total="getWarehouseTotalRecords"
          :page-size="itemsPerPage"
          :current-page="currentPageNum"
          @current-change="currentPageChange"
          background
          class="custom-pagination"
        />
      </div>
    </div>

    <template v-else>
      <ItemNotFound class="mt-56" :content="$t('empty')" />
    </template>
  </div>
</template>

<script lang="ts">
import ItemNotFound from '@/components/common/ItemNotFound.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import WarehouseList from '@/components/warehouse/WarehouseList.vue'
import DataTableMixin from '@/components/mixins/DataTableMixin.vue'
import { EXPORT_WAREHOUSES, FETCH_WAREHOUSES, SET_HAVE_NEW_EXPORT_RECORD } from '@/store/actions'
import { FILTER_DEBOUNCE_TIME } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { EExportType, ESmartBarcodeMenu } from '@/utils/enums'
import { Options } from 'vue-class-component'
import { Mixins } from 'vue-property-decorator'
import debounce from 'lodash/debounce'

interface IWarehouseSearch {
  code?: string
  sortBy?: string
  sortType?: string
  [key: string]: string | undefined
}

@Options({
  components: { PageHeader, ItemNotFound, WarehouseList },
  name: ESmartBarcodeMenu.WAREHOUSES,
})
export default class Warehouses extends Mixins(DataTableMixin) {
  dataSearch: IWarehouseSearch = {}
  debounceFilter!: () => void

  get isWarehouseLoading(): boolean {
    return this.$store.getters.isWarehouseLoading || false
  }

  get getWarehouseTotalRecords(): number {
    return this.$store.getters.getWarehouseTotalRecords || 0
  }

  get getWarehouses() {
    return this.$store.getters.getWarehouses || []
  }

  refreshWarehouseList() {
    this.debounceFilter()
  }

  currentPageChange(pageNum: number) {
    this.currentPageNum = pageNum
    this.redirectIndex()
    this.fetchList()
  }

  async exportCSV() {
    try {
      await this.$store.dispatch(EXPORT_WAREHOUSES, {
        sortBy: this.dataSearch.sortBy,
        sortType: this.dataSearch.sortType,
      })
      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.MASTER_DATA, isHaveNew: true })
    } catch (e) {
      errorHandler(e)
    }
  }

  handleSortChange(data: { prop: string; order: string | null }) {
    this.dataSearch.sortBy = data.prop
    this.dataSearch.sortType = data.order ? (data.order === 'ascending' ? 'asc' : 'desc') : undefined
    this.currentPageNum = 1
    this.redirectIndex()
    this.fetchList()
  }

  fetchList() {
    try {
      this.$store.dispatch(FETCH_WAREHOUSES, {
        page: this.currentPageNum,
        limit: this.itemsPerPage,
        code: this.dataSearch.code || '',
        sortBy: this.dataSearch.sortBy,
        sortType: this.dataSearch.sortType,
      })
    } catch (e) {
      errorHandler(e)
    }
  }

  get isMarumiyaPackage() {
    return this.$store.getters.isMarumiyaPackage
  }

  created() {
    if (this.isMarumiyaPackage) {
      this.$router.push({ name: 'home' })
      return
    }
    this.routeName = 'warehouses'
    this.getQueryData()
    this.debounceFilter = debounce(() => {
      this.currentPageNum = 1
      this.redirectIndex()
      this.fetchList()
    }, FILTER_DEBOUNCE_TIME)
    this.fetchList()
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';
.header-container-wrapper {
  background-color: $body-bg;
}

.fixed-table:deep() {
  height: calc(100vh - 100px);
  min-width: 800px;

  &.table-content {
    overflow: auto;
    height: calc(100vh - 350px);
    min-height: 200px;
  }
  &.el-table {
    &::before {
      height: 0px;
    }
    &__header-wrapper {
      position: sticky;
      position: -webkit-sticky;
      top: 0;
      z-index: 10;
    }
  }
}
</style>
